import { LitElement, html } from 'lit';
import { styles } from './css';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import {
  analyticsCtaDataObject,
  analyticsLabelFromString,
  handleAdobeNavEvent,
} from '../../../../helpers/adobe-analytics';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';
export class WalterProductsBlock extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      styles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content'));
  }

  async connectedCallback() {
    super.connectedCallback();
  }

  render() {
    return html`
      <div id="products-block">
        ${this.productData
          ? html`
              <div class="header column">
                <h2>${this.formattedHeaderStr}</h2>
                <h3>
                  Specifications & Documentation
                </h3>
                <img class="sample-img" src=${this.productData.imageUrl} />
                <cta-button
                  cta-data=${JSON.stringify(this.primaryCtaData)}
                  cta-type="primary"
                  @click="${this.requestAQuote}"
                ></cta-button>
              </div>
            `
          : html``}
        ${this.productDetailsData
          ? html`
              <div class="product column">
                <h4>Solution Details</h4>
                <div class="column-content">
                  ${this.productDetailsData.rows.map(
                    row => html`
                      <div class="row">
                        <p class="row-header">${unsafeHTML(row.header)}</p>
                        <p class="row-body">
                          ${this.renderRowContent(row.text || row.content)}
                        </p>
                      </div>
                    `
                  )}
                </div>
              </div>
            `
          : html``}
        ${this.sustainabilityData
          ? html`
              <div class="sustainability column">
                <h4>${this.sustainabilityData.title}</h4>
                <div class="column-content">
                  <div class="row">
                    <p class="row-body">${this.sustainabilityData.text}</p>
                  </div>
                  <div class="row">
                    <p class="row-header">Sustainability Documentation</p>
                    <div class="sustainability-downloads">
                      ${this.sustainabilityDownloads.length > 0
                        ? this.sustainabilityDownloads.map(sustainDownload => {
                            return html`
                              <div class="downloads-wrapper print-none">
                                <cta-text
                                  .ctaData=${sustainDownload}
                                  .ctaAnalyticsData=${analyticsCtaDataObject(
                                    sustainDownload,
                                    'Product Details'
                                  )}
                                  hide-right-arrow-svg="true"
                                  target-blank="true"
                                  left-download-svg="true"
                                ></cta-text>
                              </div>
                            `;
                          })
                        : html``}
                    </div>
                  </div>
                </div>
              </div>
            `
          : html``}
        ${this.downloadsData
          ? html`
              <div class="download column">
                <h4>${this.downloadsData.title}</h4>
                <div class="column-content">
                  ${this.downloadsList.length > 0
                    ? this.downloadsList.map(download => {
                        return html`
                          <cta-button
                            cta-data=${this.downloadCtaData(download)}
                            cta-type="secondary"
                          ></cta-button>
                        `;
                      })
                    : html``}
                </div>
              </div>
            `
          : html``}
      </div>
    `;
  }

  fallbackCtaClicked() {
    // TODO analytics will need to go here
  }

  get barrelsData() {
    return this.content.barrelsData || [];
  }

  // Barrel may be titled either 'Product Details' or 'Solution Details' in
  // the CMS. Display label is hardcoded to 'Solution Details' (see <h4>);
  // analytics strings below intentionally keep 'Product Details' as the
  // stable internal identifier, independent of the user-facing label.
  get productDetailsData() {
    return this.barrelsData.find(barrel =>
      ['Product Details', 'Solution Details'].includes(barrel.title)
    );
  }

  get sustainabilityData() {
    return this.barrelsData.find(barrel => barrel.title === 'Sustainability');
  }

  get sustainabilityDownloads() {
    return this.sustainabilityData?.downloads || [];
  }

  get downloadsData() {
    return this.barrelsData.find(barrel => barrel.title === 'Downloads');
  }

  get downloadsList() {
    return this.downloadsData.downloads;
  }

  get formattedHeaderStr() {
    return `${this.productData?.headerString || ''}`;
  }

  requestAQuote() {
    const requestMoreInfoFromBarrel = new CustomEvent(
      'requestMoreInfoFromBarrel',
      {
        bubbles: true,
        composed: true,
        detail: this.primaryCtaData,
      }
    );
    this.dispatchEvent(requestMoreInfoFromBarrel);
  }

  downloadCtaData(download) {
    download.fullWidth = true;
    download.target = '_blank';
    download.isDownload = true;
    download.placement = 'Product Details';
    return JSON.stringify(download);
  }

  get productData() {
    return this.content.productData;
  }

  get primaryCtaData() {
    return {
      text: 'Request a Quote',
      url: '/request-quote',
      fullWidth: true,
    };
  }

  renderRowContent(rowContent) {
    if (typeof rowContent !== 'object') return this.rowContentText(rowContent);

    if (Array.isArray(rowContent))
      return html`
        ${rowContent.map(this.rowContentLink.bind(this))}
      `;

    return html`
      ${this.rowContentLink(rowContent)}
    `;
  }

  rowContentLink(link) {
    return !!link && typeof link === 'object'
      ? html`
          <a
            href=${link.url || ''}
            @click="${event => this.handleRowLinkClick(link, event)}"
          >
            ${this.rowContentText(link.text || '')}
          </a>
        `
      : html``;
  }

  handleRowLinkClick(link, event) {
    if (!link.url) return;
    const placement = this.content?.productData?.name || 'Product Details';
    handleAdobeNavEvent({
      navString: `NAV|${analyticsLabelFromString(
        placement
      )}|${analyticsLabelFromString(link.text || link.url)}`,
      destinationUrl: link.url,
      event,
    });
  }

  rowContentText(text) {
    text = text || '';
    // remove any empty <p> tags coming from the CMS editor
    text = text.replaceAll('<p><br /></p>', '');
    return html`
      <p class="row-content-text">
        ${unsafeHTML(text)}
      </p>
    `;
  }
}
customElements.define('product-details-block', WalterProductsBlock);
